import { Dataset, DEFAULT_PAGE_SIZE, DtFilter, DtSort } from '@/components/data-table/helpers';
import { GroupMeta, GroupModel } from '@/lib/models/group.model';
import { JsonObject } from '../helpers';
import { UserModel } from '../models/user.model';
import { CreateResponse, DeleteResponse, ListResponse, PatchResponse } from '.';
import { get, post, patch, deleteApi, create } from './sp-api.service';
import { getAllGroupPoliciesV1 } from './policy-v1.service';
import { PolicyModelV1 } from '../models/policy-model-v1/policy-v1.model';

const USER_PATH = '/user/users';
const SAFE_LIMIT = 200;

type SearchFilter = {
  keyword?: string;
  groups?: string[];
};

export const searchUsers = async (keyword: string): Promise<UserModel[]> => {
  const response = (await post(`${USER_PATH}/search`, {
    match: {
      keyword,
    },
    page: 1,
    pageSize: SAFE_LIMIT,
  })) as ListResponse;

  return response.data.map((item) => new UserModel(item));
};

export const getEndUsers = async (
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<[Dataset<UserModel>, Map<string, GroupMeta>]> => {
  const groupMeta: Map<string, GroupMeta> = new Map();
  const { keyword, values = [] } = dtFilter || {};

  const filterParams: SearchFilter = {};

  if (keyword) {
    filterParams.keyword = keyword;
  }

  values.forEach(({ columnName, value }) => {
    switch (columnName) {
      case 'groups':
        filterParams.groups = value as string[];
        break;
    }
  });

  const response = (await post(`${USER_PATH}/search`, {
    page: page + 1,
    pageSize,
    count: true,
    sort: [dtSort ? { [dtSort.columnName]: dtSort.direction } : { name: 'asc' }],
    match: Object.keys(filterParams).length ? filterParams : undefined,
  })) as ListResponse;

  const { count: total, data } = response || {};

  if (!data) {
    return [
      {
        page,
        pageSize,
        rows: [],
        total: 0,
      },
      groupMeta,
    ];
  }

  const rows: UserModel[] = response.data.map((data) => {
    const userGroups = data.groups as JsonObject[];

    userGroups.forEach((data) => {
      groupMeta.set(data._id as string, new GroupModel(data).meta);
    });

    data.groups = userGroups.map(({ _id }) => _id);

    return new UserModel(data);
  });

  return [
    {
      page,
      pageSize,
      rows,
      total,
    },
    groupMeta,
  ];
};

export const getEndUserById = async (userId: string): Promise<[UserModel | null, GroupModel[]]> => {
  const response = await get(`${USER_PATH}/${userId}`);

  if (!response) {
    return [null, []];
  }

  const groups: GroupModel[] = [];
  const user = new UserModel(response as JsonObject);
  user.groups = user.groups.map((groupData) => {
    const group = new GroupModel(groupData as unknown as JsonObject);
    groups.push(group);
    return group.id;
  });

  return [user, groups];
};

export const createUser = async (user: UserModel): Promise<CreateResponse> => {
  const data = {
    name: user.name,
    email: user.email,
    groups: user.groups,
  };

  const response = await create(USER_PATH, data);

  if (!response.created) {
    return response;
  }

  response.model = new UserModel(response.data as JsonObject);

  return response;
};

export const patchUser = async (userId: string, data: JsonObject): Promise<PatchResponse> => {
  const [user] = await getEndUserById(userId);

  if (!user) {
    return { patched: false, error: 'User not found' };
  }

  const updateData: { name?: string; email?: string; groups?: string[] } = {};

  // once a user has signed in, you can no longer modify their name or email, because we don't currently
  // have a way to validate/sync that change with GCIP
  if (!user.lastSigninTimestamp) {
    if (data.name) {
      updateData.name = data.name as string;
    }
    if (data.email) {
      updateData.email = data.email as string;
    }
  }

  if (data.groups) {
    updateData.groups = data.groups as string[];
  }

  return await patch(`${USER_PATH}/${userId}`, updateData);
};

export const deleteUser = async (userId: string): Promise<DeleteResponse> => {
  const [user] = await getEndUserById(userId);

  if (!user) {
    return { deleted: false, error: 'User not found' };
  }

  if (!user.canDelete) {
    if (user.isExternal) {
      return { deleted: false, error: 'Cannot delete a sync user' };
    }

    if (user.lastSigninTimestamp) {
      return { deleted: false, error: 'Cannot delete a user that has signed in' };
    }

    return { deleted: false, error: 'Cannot delete user' };
  }
  return deleteApi(`${USER_PATH}/${userId}`);
};

type UserGroupUsage = {
  policies: PolicyModelV1[];
  inUse: boolean;
};

export const getEndUserPolicyUsages = async (
  users: UserModel[]
): Promise<Map<string, UserGroupUsage>> => {
  const policies = await getAllGroupPoliciesV1();
  const userUsages: Map<string, UserGroupUsage> = new Map();

  users.forEach((user) => {
    const userGroupIds = user.groups || [];
    const userPolicies = policies.filter((policy) =>
      policy.groups.some((groupId) => userGroupIds.includes(groupId))
    );
    userUsages.set(user.id, { policies: userPolicies, inUse: !!userPolicies.length });
  });

  return userUsages;
};
